import { UIComponent } from './UIComponent.js';

/**
 * Modal Component
 * Handles modal dialogs with configurable content
 */
export class Modal extends UIComponent {
  constructor(element, options = {}) {
    super(element, options);
    this.isOpen = false;
    this.backdrop = null;
  }

  getDefaultOptions() {
    return {
      ...super.getDefaultOptions(),
      closable: true,
      closeOnEscape: true,
      closeOnBackdrop: true,
      className: 'modal',
      backdropClassName: 'modal-backdrop'
    };
  }

  init() {
    super.init();
    this.createBackdrop();
    this.setupCloseHandlers();
    return this;
  }

  /**
   * Create modal backdrop
   */
  createBackdrop() {
    this.backdrop = document.createElement('div');
    this.backdrop.className = this.options.backdropClassName;
    this.backdrop.style.display = 'none';
    document.body.appendChild(this.backdrop);

    // Close on backdrop click
    if (this.options.closeOnBackdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }
  }

  /**
   * Setup close handlers
   */
  setupCloseHandlers() {
    // Close button
    const closeButton = this.element.querySelector('.modal-close');
    if (closeButton && this.options.closable) {
      closeButton.addEventListener('click', () => this.close());
    }

    // ESC key
    if (this.options.closeOnEscape) {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }
  }

  /**
   * Open modal
   */
  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.element.style.display = 'block';
    this.backdrop.style.display = 'block';

    // Add body scroll lock
    document.body.style.overflow = 'hidden';

    // Animate in
    requestAnimationFrame(() => {
      this.backdrop.classList.add('show');
      this.element.classList.add('show');
    });

    this.emit('open');
  }

  /**
   * Close modal
   */
  close() {
    if (!this.isOpen || !this.options.closable) return;

    this.isOpen = false;
    this.element.classList.remove('show');
    this.backdrop.classList.remove('show');

    // Remove body scroll lock
    document.body.style.overflow = '';

    // Hide after animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.element.style.display = 'none';
        this.backdrop.style.display = 'none';
      }
    }, 300);

    this.emit('close');
  }

  /**
   * Set modal content
   */
  setContent(content) {
    const contentElement = this.element.querySelector('.modal-content');
    if (contentElement) {
      if (typeof content === 'string') {
        contentElement.innerHTML = content;
      } else if (content instanceof HTMLElement) {
        contentElement.innerHTML = '';
        contentElement.appendChild(content);
      }
    }
  }

  /**
   * Set modal title
   */
  setTitle(title) {
    const titleElement = this.element.querySelector('.modal-title');
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  /**
   * Destroy modal
   */
  destroy() {
    if (this.backdrop && this.backdrop.parentNode) {
      this.backdrop.parentNode.removeChild(this.backdrop);
    }
    document.body.style.overflow = '';
    super.destroy();
  }

  /**
   * Show confirmation modal
   */
  static confirm(message, options = {}) {
    return new Promise((resolve) => {
      const modalTemplate = `
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${options.title || 'Confirm Action'}</h5>
              ${options.closable !== false ? '<button class="modal-close" aria-label="Close">&times;</button>' : ''}
            </div>
            <div class="modal-body">
              <p>${message}</p>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary modal-cancel">${options.cancelText || 'Cancel'}</button>
              <button class="btn btn-primary modal-confirm">${options.confirmText || 'Confirm'}</button>
            </div>
          </div>
        </div>
      `;

      const modalElement = document.createElement('div');
      modalElement.innerHTML = modalTemplate;
      const modal = new Modal(modalElement.firstElementChild, {
        closable: true,
        closeOnEscape: true,
        closeOnBackdrop: true
      });

      // Setup button handlers
      modal.element.querySelector('.modal-cancel').addEventListener('click', () => {
        modal.close();
        resolve(false);
      });

      modal.element.querySelector('.modal-confirm').addEventListener('click', () => {
        modal.close();
        resolve(true);
      });

      // Close on X button if closable
      const closeBtn = modal.element.querySelector('.modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          modal.close();
          resolve(false);
        });
      }

      // Add to DOM and show
      document.body.appendChild(modalElement);
      modal.init().open();

      // Handle modal close without confirmation
      modal.on('close', () => {
        setTimeout(() => {
          if (!modal.isDestroyed) {
            resolve(false);
            modal.destroy();
          }
        }, 100);
      });
    });
  }

  /**
   * Show alert modal
   */
  static alert(message, options = {}) {
    return new Promise((resolve) => {
      const modalTemplate = `
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${options.title || 'Notice'}</h5>
              <button class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
              <p>${message}</p>
            </div>
            <div class="modal-footer">
              <button class="btn btn-primary modal-ok">${options.buttonText || 'OK'}</button>
            </div>
          </div>
        </div>
      `;

      const modalElement = document.createElement('div');
      modalElement.innerHTML = modalTemplate;
      const modal = new Modal(modalElement.firstElementChild, options);

      modal.element.querySelector('.modal-ok').addEventListener('click', () => {
        modal.close();
        resolve();
      });

      modal.element.querySelector('.modal-close').addEventListener('click', () => {
        modal.close();
        resolve();
      });

      document.body.appendChild(modalElement);
      modal.init().open();

      modal.on('close', () => {
        setTimeout(() => {
          modal.destroy();
          resolve();
        }, 100);
      });
    });
  }
  /**
   * Show edit hazard modal
   */
  static editHazard(hazard, options = {}) {
    return new Promise((resolve) => {
      const modalTemplate = `
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${options.title || 'Gefährdung bearbeiten'}</h5>
              <button class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
              <form id="edit-hazard-form" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <div class="form-group">
                    <label class="block text-sm font-medium text-slate-700 mb-1">Bereich</label>
                    <input type="text" name="area" class="w-full rounded-lg border-slate-300 text-sm" value="${hazard.area || ''}" readonly>
                  </div>
                  <div class="form-group">
                    <label class="block text-sm font-medium text-slate-700 mb-1">Kategorie</label>
                    <input type="text" name="category" class="w-full rounded-lg border-slate-300 text-sm" value="${hazard.category || ''}">
                  </div>
                </div>
                
                <div class="form-group">
                  <label class="block text-sm font-medium text-slate-700 mb-1">Beschreibung</label>
                  <textarea name="description" rows="3" class="w-full rounded-lg border-slate-300 text-sm">${hazard.description || ''}</textarea>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div class="form-group">
                    <label class="block text-sm font-medium text-slate-700 mb-1">Risiko</label>
                    <select name="riskLevel" class="w-full rounded-lg border-slate-300 text-sm">
                      <option value="hoch" ${hazard.riskLevel === 'hoch' ? 'selected' : ''}>Hoch</option>
                      <option value="mittel" ${hazard.riskLevel === 'mittel' ? 'selected' : ''}>Mittel</option>
                      <option value="gering" ${hazard.riskLevel === 'gering' ? 'selected' : ''}>Gering</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label class="block text-sm font-medium text-slate-700 mb-1">Priorität</label>
                    <select name="priority" class="w-full rounded-lg border-slate-300 text-sm">
                      <option value="sofort" ${hazard.remediation?.priority === 'sofort' ? 'selected' : ''}>Sofort</option>
                      <option value="fristig" ${hazard.remediation?.priority === 'fristig' ? 'selected' : ''}>Fristig</option>
                      <option value="mittel" ${hazard.remediation?.priority === 'mittel' ? 'selected' : ''}>Mittel</option>
                      <option value="langfristig" ${hazard.remediation?.priority === 'langfristig' ? 'selected' : ''}>Langfristig</option>
                    </select>
                  </div>
                </div>

                <div class="form-group">
                  <label class="block text-sm font-medium text-slate-700 mb-1">Massnahmen</label>
                  <textarea name="measures" rows="3" class="w-full rounded-lg border-slate-300 text-sm">${(hazard.remediation?.measures || []).join('\n')}</textarea>
                  <p class="text-xs text-slate-500 mt-1">Eine Massnahme pro Zeile</p>
                </div>

                <div class="form-group">
                  <label class="block text-sm font-medium text-slate-700 mb-1">Kostenschätzung</label>
                  <input type="text" name="costEstimate" class="w-full rounded-lg border-slate-300 text-sm" value="${hazard.remediation?.costEstimate || ''}">
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary modal-cancel">Abbrechen</button>
              <button class="btn btn-primary modal-save">Speichern</button>
            </div>
          </div>
        </div>
      `;

      const modalElement = document.createElement('div');
      modalElement.innerHTML = modalTemplate;
      // Ensure modal class is added to the wrapper
      modalElement.className = 'modal';

      const modal = new Modal(modalElement, {
        closable: true,
        closeOnEscape: true,
        closeOnBackdrop: true
      });

      // Setup handlers
      modal.element.querySelector('.modal-cancel').addEventListener('click', () => {
        modal.close();
        resolve(null);
      });

      modal.element.querySelector('.modal-save').addEventListener('click', () => {
        const form = modal.element.querySelector('form');
        const formData = new FormData(form);

        const updatedHazard = {
          ...hazard,
          category: formData.get('category'),
          description: formData.get('description'),
          riskLevel: formData.get('riskLevel'),
          remediation: {
            ...hazard.remediation,
            priority: formData.get('priority'),
            measures: formData.get('measures').split('\n').filter(line => line.trim() !== ''),
            costEstimate: formData.get('costEstimate')
          }
        };

        modal.close();
        resolve(updatedHazard);
      });

      const closeBtn = modal.element.querySelector('.modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          modal.close();
          resolve(null);
        });
      }

      document.body.appendChild(modalElement);
      modal.init().open();

      modal.on('close', () => {
        setTimeout(() => {
          if (!modal.isDestroyed) {
            modal.destroy();
            // Resolve with null if closed without saving (and not already resolved)
            // This might be redundant if buttons resolve, but good for safety
          }
        }, 100);
      });
    });
  }
}
